<template>
  <div class="content-page">
    <div>
      <TheBreadcrumb :pages="arianeRoute" />
      <NuxtLazyHydrate when-idle>
        <HeaderContent
          v-if="!source"
          :page="currentPageInfo"
        />
        <HeaderSource
          v-else
          :page="currentPageInfo"
        />
      </NuxtLazyHydrate>
      <div class="post-container">
        <div
          v-if="currentPageInfo.favicon && source"
          class="favicon"
        >
          <img
            :src="currentPageInfo.favicon.url"
            :alt="`favicon de ${currentPageInfo.title}`"
          />
        </div>
        <PostItem
          :source="source"
          :side-content="sideContent"
          :post-content="postContent"
        />
      </div>
      <BlueSection
        :connected="authenticated"
        diagonal="none"
        class="blue-section-item"
        :challenges="monthlyChallenges"
      />
      <DoNotMissSection
        v-if="doNotMiss.length > 0"
        :slider-items="doNotMiss"
      />
      <OfferSection
        v-if="offer"
        :offer="offer"
        >Acheter ce jeu</OfferSection
      >
      <section
        v-if="otherCommunities.length > 0"
        class="other-communities section"
      >
        <div class="title-line line">
          <h2 class="title-strat">ces communautés peuvent vous intéresser</h2>
          <NuxtLink
            to="/communautes"
            class="button-more"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 19.328 34.43"
            >
              <path
                d="M18.71 15.72 3.66.62a2.178 2.178 0 0 0-3.041 0 2.178 2.178 0 0 0 0 3.041l13.555 13.555L.619 30.77a2.178 2.178 0 0 0 0 3.041 2.178 2.178 0 0 0 3.041 0l15.05-15.05a2.178 2.178 0 0 0 0-3.041Z"
                fill="#fff"
                fill-rule="evenodd"
              />
            </svg>
            Plus de communautés
          </NuxtLink>
        </div>
        <div class="slider">
          <CommunitySlider :communities="otherCommunities" />
        </div>
      </section>
      <p class="mention">
        * Récompense provisoire sous réserve de maintenir votre place au
        classement jusqu'à la fin du mois.
      </p>
    </div>
    <PegiWarning v-if="waperCorner?.community?.pegi === '18'" />
  </div>
</template>

<script setup lang="ts">
import type { Category4 } from '~/types'

definePageMeta({
  middleware: async (to) => {
    const loadWaperCornerCommunitySubCategoryPostTitle =
      useLoadWapersCornerCommunitySubCategoryPostTitle()
    const waperCornersStore = useWaperCornersStore()
    const redirectMovedPost = useRedirectMovedPost()

    const [result] = await Promise.all([
      waperCornersStore.fetchWaperCornersCommunitySubcategories(
        to.params.communityName as string,
      ),
      loadWaperCornerCommunitySubCategoryPostTitle(to),
    ])
    return result ?? redirectMovedPost(to)
  },
})

const challengesStore = useChallengesStore()
const { monthlyChallenges } = storeToRefs(challengesStore)

const slidersStore = useDoNotMissStore()
const { doNotMiss } = storeToRefs(slidersStore)

const route = useRoute()

const waperCornersStore = useWaperCornersStore()
const {
  waperCorner,
  otherWaperCorners,
  communityWaperCornersSubcategoriesList,
} = storeToRefs(waperCornersStore)

if (
  waperCorner.value?.category.slug !== 'le-coin-des-wapers' ||
  waperCorner.value?.community?.slug !== route.params.communityName
) {
  throw createError({ statusCode: usePostGoneState().value ? 410 : 404 })
}

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: waperCorner.value?.community?.title,
      route: `/communautes/${waperCorner.value?.community?.slug}`,
    },
    {
      title: renameWapersCornerCategory(waperCorner.value?.category.name),
      route: `/communautes/${waperCorner.value?.community?.slug}/${renameWapersCornerUrl(waperCorner.value?.category.slug)}`,
    },
    {
      title: waperCorner.value?.sub_category?.name,
      route: `/communautes/${waperCorner.value?.community?.slug}/${renameWapersCornerUrl(waperCorner.value?.category.slug)}/${waperCorner.value?.sub_category?.slug}`,
    },
    {
      title: waperCorner.value?.title,
      route: ``,
    },
  ]
})

const source = ref(waperCorner.value?.type === 'source')

const newsArticleSchema = useNewsArticleSchema({
  post: waperCorner.value,
  source,
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: waperCorner.value?.title_seo,
  script: [newsArticleSchema, breadcrumbSchema],
})

useSeoMeta({
  // posts titled with a pasted URL or web search (Post::isSeoThin): kept out of Google
  robots: waperCorner.value?.seo_noindex ? 'noindex, follow' : undefined,
  title: waperCorner.value?.title_seo,
  description: waperCorner.value?.description_seo,
  keywords: waperCorner.value?.keywords_seo,
  ogTitle: waperCorner.value?.title_og,
  ogType: 'article',
  articlePublishedTime: waperCorner.value?.created_at,
  articleModifiedTime:
    waperCorner.value?.updated_at || waperCorner.value?.created_at,
  articleAuthor: waperCorner.value?.user?.nickname
    ? [waperCorner.value.user.nickname]
    : undefined,
  ogDescription: waperCorner.value?.description_og,
  ogImage:
    waperCorner.value?.image_og?.url ??
    'https://www.weareplaystation.fr/img/og-img.jpg',
})

const currentPageInfo = computed(() => {
  const rawWaperCorner = unref(waperCorner)
  const data = {
    title: rawWaperCorner?.title,
    contentType: rawWaperCorner?.type,
    type: rawWaperCorner?.category.name,
    pegi: rawWaperCorner?.community?.pegi,
    image: rawWaperCorner?.image?.url,
    imageLegend: rawWaperCorner?.image_legend,
    content: undefined,
    subject: undefined,
    favicon: undefined,
  }

  if (rawWaperCorner?.type === 'source') {
    return {
      ...data,
      favicon: rawWaperCorner?.favicon,
      urlVideo: rawWaperCorner?.embed,
    }
  } else {
    return {
      ...data,
      subject: rawWaperCorner?.community?.title,
      urlVideo: rawWaperCorner?.video,
      content: rawWaperCorner?.content,
    }
  }
})

const slug = computed(() => {
  return route.params.postTitle as string
})

const socialStore = useSocialStore()
const { social } = storeToRefs(socialStore)

const popoutStore = usePopoutStore()
const { popout } = popoutStore

const postContent = computed(() => {
  const rawWaperCorner = unref(waperCorner)
  const rawSocial = unref(social)

  return {
    firstTag: {
      title: rawWaperCorner?.community?.title,
      slug: rawWaperCorner?.community?.slug,
    },
    type: rawWaperCorner?.category.name,
    createdAt: rawWaperCorner?.created_at,
    updatedAt: rawWaperCorner?.updated_at,
    sourceAuthor: rawWaperCorner?.source_author,
    sourceAuthorUrl: rawWaperCorner?.source_author_url,
    slugType: rawWaperCorner?.category.slug,
    description: rawWaperCorner?.description,
    url: rawWaperCorner?.link,
    title: rawWaperCorner?.title,
    user: rawWaperCorner?.user,
    pegi: rawWaperCorner?.community?.pegi,
    followed: rawSocial.is_following,
    content: rawWaperCorner?.content,
    contentImages: rawWaperCorner?.content_images,
    contentEmbed: rawWaperCorner?.content_embed,
    tags: rawWaperCorner?.tags,
    likes: rawSocial.likes_count,
    shares: rawSocial.shares_count,
    comments: rawSocial.comments,
    commentsCount: rawSocial.comments_count,
    has_more_comments: rawSocial.has_more_comments,
    is_liked: rawSocial.is_liked,
    slug: slug.value,
    urlVideo: rawWaperCorner?.video,
    popout,
  }
})

const { offer, contentBlocksType } = useOffer(waperCorner)

const sideContent = computed(() => {
  const navSide: Category4[] = [{ name: 'La communauté', slug: '' }]

  contentBlocksType.value?.forEach(function (element) {
    if (element?.category) {
      const categorySlug = element.category.slug

      if (categorySlug === 'le-coin-des-wapers') {
        navSide.push({
          name: 'Le feed des players',
          slug: 'le-feed-des-players',
        })
      } else if (
        categorySlug === 'actualites-communaute' ||
        categorySlug === 'avis-joueurs' ||
        categorySlug === 'avis-medias' ||
        categorySlug === 'astuces'
      ) {
        navSide.push({ ...element.category })
      }
    } else if (element?.slug === 'wapers') {
      navSide.push({ name: 'Les Wapers influents', slug: 'wapers' })
    }
  })

  return {
    title: waperCorner.value?.community?.title,
    type: waperCorner.value?.category.name,
    route: `/communautes/${waperCorner.value?.community?.slug}`,
    nav: navSide,
    cards: otherWaperCorners.value,
    subCategoriesFilters: true,
    subCategories: communityWaperCornersSubcategoriesList.value,
    navName: 'contenus Wapers',
  }
})

const otherCommunities = computed(() => {
  const communityBlock = contentBlocksType.value?.find(
    (block) => block?.slug === 'communautes',
  )

  if (!communityBlock?.communities) return []

  return communityBlock.communities.map((item) => ({
    ...item,
    route: `/communautes/${item.slug}`,
  }))
})

watch(waperCorner, (newValue) => {
  if (newValue?.category.slug !== 'le-coin-des-wapers') {
    throw createError({ statusCode: 404 })
  }
})

const { authenticated } = useAuthenticated()

const loadWaperCornerCommunitySubCategoryPostTitle =
  useLoadWapersCornerCommunitySubCategoryPostTitle()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadWaperCornerCommunitySubCategoryPostTitle(route)
  }
})
</script>

<style lang="scss" scoped>
.content-page {
  .post-container {
    position: relative;

    .favicon {
      position: absolute;
      top: -4.4rem;
      left: 8rem;
      z-index: 2;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 8.4rem;
      height: 8.4rem;
      background: #fff;
      border-radius: 4.2rem;

      @media screen and (max-width: 430px) {
        left: 2.5rem;
        width: 7rem;
        height: 7rem;
        border-radius: 3.5rem;
      }

      img {
        width: 5.6rem;
        height: 3.8rem;
        object-fit: contain;
      }
    }
  }

  .mention {
    margin-top: 10rem;
  }
}

@media screen and (max-width: 700px) {
  .content-page {
    .other-communities {
      padding: 0;
    }

    .mention {
      margin: 2.5em;
    }
  }
}
</style>
